import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  BarChart3,
  Calendar,
  CreditCard,
  Banknote,
  Smartphone,
  Package,
  FileText,
  Receipt,
  User,
  Printer,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { formatCurrency } from './posUtils';

const ZReadingDialog = ({
  open,
  onOpenChange,
  zReadingDate,
  setZReadingDate,
  loadZReading,
  loadingZReading,
  zReadingData,
  compact = false, // simplified version for "no shift" screen
}) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Z-Reading - ${zReadingDate}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            h1 { text-align: center; font-size: 18px; margin-bottom: 5px; }
            h2 { text-align: center; font-size: 14px; color: #666; margin-bottom: 20px; }
            .summary { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
            .summary-item { padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
            .summary-item label { font-size: 10px; color: #666; display: block; }
            .summary-item value { font-size: 16px; font-weight: bold; }
            .section { margin-bottom: 15px; }
            .section-title { font-weight: bold; font-size: 12px; border-bottom: 1px solid #000; padding-bottom: 5px; margin-bottom: 10px; }
            .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dotted #ccc; font-size: 12px; }
            .row:last-child { border-bottom: none; }
            .total-row { font-weight: bold; border-top: 2px solid #000; margin-top: 10px; padding-top: 10px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #999; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>NAS - DAILY SALES REPORT</h1>
          <h2>Z-Reading: ${new Date(zReadingDate).toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h2>
          
          <div class="summary">
            <div class="summary-item">
              <label>GROSS SALES</label>
              <value>₱${(zReadingData?.grossSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</value>
            </div>
            <div class="summary-item">
              <label>NET SALES</label>
              <value>₱${(zReadingData?.netSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</value>
            </div>
            <div class="summary-item">
              <label>TRANSACTIONS</label>
              <value>${zReadingData?.totalTransactions || 0}</value>
            </div>
            <div class="summary-item">
              <label>DISCOUNTS</label>
              <value>₱${(zReadingData?.totalDiscounts || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</value>
            </div>
          </div>
          
          ${zReadingData?.paymentBreakdown?.length > 0 ? `
          <div class="section">
            <div class="section-title">PAYMENT BREAKDOWN</div>
            ${zReadingData.paymentBreakdown.map(p => `
              <div class="row">
                <span>${p.method.toUpperCase()} (${p.count})</span>
                <span>₱${(p.total || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          ${zReadingData?.topProducts?.length > 0 ? `
          <div class="section">
            <div class="section-title">PRODUCTS SOLD (${zReadingData.topProducts.length})</div>
            ${zReadingData.topProducts.map(p => `
              <div class="row">
                <span>${p.name} x${p.quantity}</span>
                <span>₱${(p.total || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          ${zReadingData?.cashierBreakdown?.length > 0 ? `
          <div class="section">
            <div class="section-title">SALES BY CASHIER</div>
            ${zReadingData.cashierBreakdown.map(c => `
              <div class="row">
                <span>${c.name} (${c.count} txn)</span>
                <span>₱${(c.total || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          ${zReadingData?.shifts?.length > 0 ? `
          <div class="section">
            <div class="section-title">SHIFT SUMMARY</div>
            ${zReadingData.shifts.map(s => `
              <div class="row">
                <span>${s.cashier}</span>
                <span>₱${(s.totalSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          <div class="total-row row">
            <span>TOTAL NET SALES</span>
            <span>₱${(zReadingData?.netSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
          
          <div class="footer">
            <p>Printed: ${new Date().toLocaleString('en-PH')}</p>
            <p>Nenen's Agrivet Supply</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 size={20} />
            Z-Reading - Daily Sales Report
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Date Selector */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-slate-500" />
              <Input
                type="date"
                value={zReadingDate}
                onChange={(e) => {
                  setZReadingDate(e.target.value);
                  loadZReading(e.target.value);
                }}
                className="w-44"
              />
            </div>
            <Button 
              onClick={() => loadZReading(zReadingDate)} 
              variant="outline" 
              size="sm"
              disabled={loadingZReading}
            >
              {loadingZReading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={16} />}
            </Button>
          </div>

          {loadingZReading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-600" />
              <p className="text-slate-500 mt-2">Loading report...</p>
            </div>
          ) : zReadingData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 font-medium">GROSS SALES</p>
                  <p className="text-xl font-bold text-emerald-700">{formatCurrency(zReadingData.grossSales)}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">NET SALES</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(zReadingData.netSales)}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">TRANSACTIONS</p>
                  <p className="text-xl font-bold text-purple-700">{zReadingData.totalTransactions}</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-600 font-medium">DISCOUNTS</p>
                  <p className="text-xl font-bold text-amber-700">{formatCurrency(zReadingData.totalDiscounts)}</p>
                </div>
              </div>
              
              {/* Refunds/Exchanges Row */}
              {(zReadingData.totalRefunds > 0 || zReadingData.totalExchanges > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                    <p className="text-xs text-red-600 font-medium">REFUNDS ({zReadingData.refundCount || 0})</p>
                    <p className="text-xl font-bold text-red-700">-{formatCurrency(zReadingData.totalRefunds)}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <p className="text-xs text-orange-600 font-medium">EXCHANGES ({zReadingData.exchangeCount || 0})</p>
                    <p className="text-xl font-bold text-orange-700">{formatCurrency(zReadingData.totalExchanges)}</p>
                  </div>
                </div>
              )}

              {/* Full mode shows extra sections */}
              {!compact && (
                <>
                  {/* Payment Breakdown */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <CreditCard size={18} />
                      Payment Method Breakdown
                    </h4>
                    <div className="space-y-2">
                      {zReadingData.paymentBreakdown?.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                          <div className="flex items-center gap-2">
                            {payment.method === 'cash' && <Banknote size={16} className="text-emerald-600" />}
                            {payment.method === 'gcash' && <Smartphone size={16} className="text-blue-600" />}
                            {payment.method === 'card' && <CreditCard size={16} className="text-purple-600" />}
                            <span className="font-medium capitalize">{payment.method}</span>
                            <span className="text-slate-500 text-sm">({payment.count} txn)</span>
                          </div>
                          <span className="font-semibold">{formatCurrency(payment.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cashier Breakdown */}
                  {zReadingData.cashierBreakdown?.length > 0 && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <User size={18} />
                        Sales by Cashier
                      </h4>
                      <div className="space-y-2">
                        {zReadingData.cashierBreakdown.map((cashier, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                            <div>
                              <span className="font-medium">{cashier.name}</span>
                              <span className="text-slate-500 text-sm ml-2">({cashier.count} txn)</span>
                            </div>
                            <span className="font-semibold">{formatCurrency(cashier.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Items Sold - Detailed List */}
                  {zReadingData.sales?.length > 0 && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Package size={18} />
                        Items Sold - Detailed List
                      </h4>
                      <div className="space-y-1 max-h-80 overflow-y-auto">
                        {zReadingData.sales
                          .flatMap(sale => 
                            (sale.items || []).map(item => ({
                              ...item,
                              time: sale.time,
                              receiptNo: sale.receiptNumber
                            }))
                          )
                          .sort((a, b) => new Date(a.time) - new Date(b.time))
                          .map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center py-2 px-2 bg-white rounded border-b border-slate-100 hover:bg-slate-50">
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-slate-400 w-16">
                                  {new Date(item.time).toLocaleTimeString('en-PH', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}
                                </span>
                                <div>
                                  <span className="font-medium text-sm">{item.productName}</span>
                                  <span className="text-slate-500 text-xs ml-2">
                                    {item.quantity} {item.unit || 'pcs'} @ {formatCurrency(item.unitPrice)}
                                  </span>
                                </div>
                              </div>
                              <span className="font-semibold text-emerald-600">{formatCurrency(item.totalPrice)}</span>
                            </div>
                          ))}
                      </div>
                      <div className="mt-2 pt-2 border-t flex justify-between text-sm font-medium">
                        <span>Total Items: {zReadingData.sales.reduce((sum, sale) => sum + (sale.items?.length || 0), 0)}</span>
                        <span className="text-emerald-600">{formatCurrency(zReadingData.netSales)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Product Summary */}
              {zReadingData.topProducts?.length > 0 && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Package size={18} />
                    {compact ? `All Products Sold (${zReadingData.topProducts.length})` : `Product Summary (${zReadingData.topProducts.length} products)`}
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {zReadingData.topProducts.map((product, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                        <div>
                          <span className="font-medium">{product.name}</span>
                          <span className="text-slate-500 text-sm ml-2">x{product.quantity} {product.unit || ''}</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(product.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shift Summary (full mode only) */}
              {!compact && zReadingData.shifts?.length > 0 && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText size={18} />
                    Shift Summary
                  </h4>
                  <div className="space-y-2">
                    {zReadingData.shifts.map((shift, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                        <div>
                          <span className="font-medium">{shift.cashier}</span>
                          <span className="text-slate-500 text-sm ml-2">
                            {new Date(shift.startTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' })} - 
                            {shift.endTime ? new Date(shift.endTime).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' }) : 'Active'}
                          </span>
                        </div>
                        <span className="font-semibold">{formatCurrency(shift.totalSales)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Sales Transactions Table (full mode only) */}
              {!compact && zReadingData.sales?.length > 0 && (
                <div className="bg-white border rounded-lg">
                  <h4 className="font-semibold text-slate-700 p-4 border-b flex items-center gap-2 bg-slate-50 rounded-t-lg">
                    <Receipt size={18} />
                    All Sales Transactions ({zReadingData.sales.length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Time</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Receipt #</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Cashier</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-600">Items</th>
                          <th className="text-right py-2 px-3 font-medium text-slate-600">Amount</th>
                          <th className="text-center py-2 px-3 font-medium text-slate-600">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zReadingData.sales.map((sale, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3">
                              {new Date(sale.time).toLocaleTimeString('en-PH', { 
                                hour: '2-digit', minute: '2-digit', hour12: true 
                              })}
                            </td>
                            <td className="py-2 px-3 font-mono text-xs">{sale.receiptNumber}</td>
                            <td className="py-2 px-3">{sale.cashier}</td>
                            <td className="py-2 px-3">
                              <div className="space-y-0.5">
                                {sale.items?.map((item, i) => (
                                  <div key={i} className="text-xs text-slate-600">
                                    {item.productName} x{item.quantity} {item.unit && `(${item.unit})`}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(sale.totalAmount)}</td>
                            <td className="py-2 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                sale.paymentMethod === 'cash' ? 'bg-emerald-100 text-emerald-700' :
                                sale.paymentMethod === 'gcash' ? 'bg-blue-100 text-blue-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {sale.paymentMethod?.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-100 font-semibold">
                        <tr>
                          <td colSpan="4" className="py-2 px-3 text-right">TOTAL:</td>
                          <td className="py-2 px-3 text-right text-emerald-700">{formatCurrency(zReadingData.netSales)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Report Footer (full mode only) */}
              {!compact && (
                <div className="text-center text-xs text-slate-500 pt-4 border-t">
                  <p>Report generated: {new Date().toLocaleString('en-PH')}</p>
                  <p>Nenen's Agrivet Supply</p>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 text-center text-slate-500">
              {compact ? (
                <>
                  <BarChart3 size={48} className="mx-auto text-slate-300 mb-4" />
                  <p>No sales data for this date</p>
                </>
              ) : (
                <>
                  <FileText className="h-12 w-12 mx-auto text-slate-300" />
                  <p className="mt-2">No sales data for this date</p>
                </>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {zReadingData && (
            <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
              <Printer size={compact ? 16 : 18} className="mr-2" />
              {compact ? 'Print Z-Reading' : 'Print Report'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ZReadingDialog;
